import * as React from 'react';
import { useThemeUI } from '@theme-ui/core';
import { Box, BoxProps } from '@theme-ui/components';
import { getSpace } from './_commons/spacing';

type InheritedProps = Omit<BoxProps, 'ref'>;

export interface TilesProps extends InheritedProps {
  gap?: number | string | number[] | string[];
  colGap?: number;
  rowGap?: number;
  columns?: number | number[];
  selectChildrenWithoutType?: boolean;
}

export const Tiles = React.forwardRef<HTMLDivElement, TilesProps>(
  ({ gap = 0, colGap, rowGap, columns = 1, ...props }, ref) => {
    const { theme } = useThemeUI();
    const itemCount = React.Children.count(props.children);
    const gridColumnGap = colGap ?? gap;
    const gridRowGap = rowGap ?? gap;

    let rows: number | number[];
    let gridTemplateColumns;

    let msGridColumns;
    let msGridRows;

    const childrenSelector = props.selectChildrenWithoutType ? '*:nth-child' : 'div:nth-of-type';

    // Get the number of rows necessary to place all items.
    // We cannot rely on implicit grid behavior, so we can use it in IE 10-11.
    const computeRows = (col: number) => Math.ceil(itemCount / col);
    const computeRowPosition = (index: number, cols: number) => 2 * Math.ceil(index / cols) - 1;
    const computeColumnPosition = (index: number, cols: number) =>
      2 * (index % cols === 0 ? cols : index % cols) - 1;

    const columnsTemplate = (n: number) => `repeat(${n}, 1fr)`;

    // There is no gap property in Internet Explorer.
    // We have to fake it with additional columns
    const msColumnsTemplate = (n: number) => {
      return Array(n)
        .fill('1fr')
        .join(` ${getSpace(theme, gridColumnGap)} `);
    };
    const msRowsTemplate = (n: number) => {
      return Array(n)
        .fill('auto')
        .join(` ${getSpace(theme, gridRowGap)} `);
    };

    const childrenPositions: any = {};

    if (typeof columns === 'number') {
      rows = computeRows(columns);
      gridTemplateColumns = columnsTemplate(columns);
      msGridColumns = msColumnsTemplate(columns);
      msGridRows = msRowsTemplate(rows);
      for (let i = 1; i <= itemCount; i++) {
        childrenPositions[`> ${childrenSelector}(${i})`] = {
          msGridRow: computeRowPosition(i, columns),
          msGridColumn: computeColumnPosition(i, columns)
        };
      }
    } else {
      rows = columns.map(computeRows);
      gridTemplateColumns = columns.map(columnsTemplate);
      msGridColumns = columns.map(msColumnsTemplate);
      msGridRows = rows.map(msRowsTemplate);
      for (let i = 1; i <= itemCount; i++) {
        childrenPositions[`> ${childrenSelector}(${i})`] = {
          msGridRow: columns.map((cols) => computeRowPosition(i, cols)),
          msGridColumn: columns.map((cols) => computeColumnPosition(i, cols))
        };
      }
    }

    return (
      <Box
        ref={ref}
        {...props}
        // @ts-ignore
        __themeKey="grids"
        __css={{
          '@supports (display: grid)': {
            display: 'grid',
            gridColumnGap,
            gridRowGap,
            gridTemplateColumns
          },
          display: '-ms-grid',
          msGridColumns,
          msGridRows,
          ...childrenPositions
        }}
      />
    );
  }
);
